import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useFormikContext } from 'formik'
import { getSearchableManageMembers } from '../../../../services/manageMemberService'
import { KTSVG, toAbsoluteUrl } from '../../../../../_metronic/helpers'
import { useNearBottom } from '../../../../hooks/utils/useNearBottom'

type Member = {
  id: number
  name: string
  image?: string
  email?: string
  hasRealName: boolean
}

type ApiMember = {
  id: number
  firstName?: string
  lastName?: string
  email?: string
  profilePicture?: string | null
}

type Props = {
  values: number[]
  onChange: (val: number[]) => void
  placeholder?: string
  minChars?: number
  name?: string
  // Preloaded members to seed the chip cache when opening in edit mode.
  preloaded?: ApiMember[]
}

const LIMIT = 20
const FALLBACK_AVATAR = toAbsoluteUrl('/media/svg/nobilis/user_nb-gray.svg')

const normalize = (u: ApiMember): Member => {
  const first = (u.firstName || '').trim()
  const last = (u.lastName || '').trim()
  const fullName = `${first} ${last}`.trim()
  const hasRealName = fullName.length > 0
  return {
    id: u.id,
    name: hasRealName ? fullName : (u.email || `#${u.id}`),
    email: u.email,
    image: u.profilePicture || undefined,
    hasRealName,
  }
}

export function MembersAutocompleteField({
  values = [],
  onChange,
  placeholder = 'Search members…',
  minChars = 0,
  name,
  preloaded,
}: Props) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [debounced, setDebounced] = useState('')
  const [items, setItems] = useState<Member[]>([])
  const [count, setCount] = useState(0)
  const [offset, setOffset] = useState(0)
  const [loading, setLoading] = useState(false)
  const [selectedCache, setSelectedCache] = useState<Record<number, Member>>({})
  const formik = useFormikContext()

  const reqRef = useRef(0)
  const hasMore = items.length < count

  useEffect(() => {
    const id = setTimeout(() => setDebounced(query.trim()), 300)
    return () => clearTimeout(id)
  }, [query])

  const fetchData = useCallback(async (search: string, nextOffset: number) => {
    const reqId = ++reqRef.current
    setLoading(true)
    try {
      const res: any = await getSearchableManageMembers(search, '', LIMIT, nextOffset)
      if (reqId !== reqRef.current) return
      const raw: ApiMember[] = res?.results ?? []
      const results = raw.map(normalize)
      setCount(Number(res?.count ?? results.length))
      setItems((prev) => (nextOffset === 0 ? results : [...prev, ...results]))
    } catch {
      if (reqId === reqRef.current && nextOffset === 0) {
        setItems([])
        setCount(0)
      }
    } finally {
      if (reqId === reqRef.current) setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (debounced.length < minChars) return
    setOffset(0)
    fetchData(debounced, 0)
  }, [debounced, minChars, fetchData])

  const loadMore = useCallback(() => {
    if (loading || !hasMore) return
    const next = offset + LIMIT
    setOffset(next)
    fetchData(debounced, next)
  }, [loading, hasMore, offset, debounced, fetchData])

  const handleScroll = useNearBottom(loadMore)

  useEffect(() => {
    setSelectedCache((prev) => {
      const nextCache = { ...prev }
      for (const it of items) if (values.includes(it.id)) nextCache[it.id] = it
      return nextCache
    })
  }, [items, values])

  useEffect(() => {
    if (!preloaded?.length) return
    setSelectedCache((prev) => {
      const nextCache = { ...prev }
      for (const raw of preloaded) {
        const m = normalize(raw)
        if (!nextCache[m.id]) nextCache[m.id] = m
      }
      return nextCache
    })
  }, [preloaded])

  const handleAdd = (itemId: number) => {
    if (!values.includes(itemId)) onChange([...values, itemId])
    setQuery('')
    setOpen(false)
  }

  const handleRemove = (itemId: number) => {
    onChange(values.filter((v) => v !== itemId))
  }

  const validateForm = () => {
    if (name) {
      formik.setFieldTouched(name, true)
      formik.validateForm()
    }
  }

  const selectedObjects = values
    .map((id) => selectedCache[id])
    .filter(Boolean) as Member[]

  return (
    <div className='nb-language-autocomplete'>
      <div className='nb-language-wrapper'>
        {selectedObjects.map((item) => (
          <div key={item.id} className='nb-admin-team-item-select'>
            <div className='tap-add-experience-2-image'>
              <img src={item.image || FALLBACK_AVATAR} width={24} height={24} alt='avatar' />
            </div>
            <span>{item.name}</span>
            <div onClick={() => handleRemove(item.id)}>
              <KTSVG path={toAbsoluteUrl('/media/svg/nobilis/remove.svg')} />
            </div>
          </div>
        ))}

        <input
          className='tap-add-experience-input-category nb-language-input'
          placeholder={placeholder}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value)
            setOpen(true)
            validateForm()
          }}
          onFocus={() => {
            setOpen(true)
            validateForm()
          }}
          onBlur={() => setTimeout(() => setOpen(false), 120)}
        />
      </div>

      {open && (
        <div className='nb-language-menu' onScroll={handleScroll}>
          {items.length === 0 && !loading && (
            <div className='empty-state'>No members found</div>
          )}

          {items.map((item) => (
            <div
              className='tap-add-experience-2-invitee-card cursor-pointer'
              key={item.id}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleAdd(item.id)}
            >
              <img
                className='tap-add-experience-2-invitee-card__avatar'
                src={item.image || FALLBACK_AVATAR}
                alt='avatar'
              />
              <div className='tap-add-experience-2-invitee-card__name'>{item.name}</div>
              {item.hasRealName && item.email && (
                <div className='tap-add-experience-2-invitee-card__role-wrapper'>
                  <div className='tap-add-experience-2-invitee-card__role'>{item.email}</div>
                </div>
              )}
            </div>
          ))}

          {loading && (
            <div className='tap-add-experience-2-invitee-card' style={{ color: '#808080' }}>
              Loading…
            </div>
          )}
        </div>
      )}
    </div>
  )
}
